import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, EffectFade, Autoplay } from "swiper/modules";
import { ArrowLeft, ArrowRight } from "lucide-react";
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/navigation";
import { useRef } from "react";

import SectionHeading from "./SectionHeading";
import { projects } from "../data/site";

export default function ProjectsSection() {
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  return (
    <section
      id="projects"
      data-testid="projects-section"
      className="relative py-28 md:py-40"
    >
      <div className="px-6 md:px-12">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <SectionHeading
            eyebrow="Selected Projects"
            title="Where regulation becomes architecture."
            testId="projects-heading"
          />
          <div className="flex gap-3">
            <button
              ref={prevRef}
              data-testid="projects-prev"
              className="h-12 w-12 rounded-full border border-white/10 grid place-items-center hover:border-[#C96A2B] hover:text-[#C96A2B] transition-colors"
              aria-label="Previous project"
            >
              <ArrowLeft size={18} />
            </button>
            <button
              ref={nextRef}
              data-testid="projects-next"
              className="h-12 w-12 rounded-full border border-white/10 grid place-items-center hover:border-[#C96A2B] hover:text-[#C96A2B] transition-colors"
              aria-label="Next project"
            >
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </div>

      <Swiper
        modules={[Navigation, EffectFade, Autoplay]}
        effect="fade"
        speed={1200}
        loop
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        navigation={{ prevEl: prevRef.current, nextEl: nextRef.current }}
        onBeforeInit={(swiper) => {
          swiper.params.navigation.prevEl = prevRef.current;
          swiper.params.navigation.nextEl = nextRef.current;
        }}
        className="!w-full"
      >
        {projects.map((p, idx) => (
          <SwiperSlide key={p.title}>
            <div
              data-testid={`project-slide-${idx}`}
              className="relative w-full h-[70vh] md:h-[78vh]"
            >
              {p.video ? (
                <video
                  src={p.video + "#t=0.1"}
                  preload="metadata"
                  playsInline
                  muted
                  className="absolute inset-0 h-full w-full object-cover"
                />
              ) : (
                <img
                  src={p.img}
                  alt={p.title}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/40 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-16">
                <p className="eyebrow">
                  0{idx + 1} / {String(projects.length).padStart(2, "0")} · {p.sector}
                </p>
                <h3 className="font-display text-4xl md:text-6xl lg:text-7xl mt-4 max-w-3xl leading-[1.05]">
                  {p.title}
                </h3>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  );
}
